// frontend/src/components/UserEmailSettings.tsx
import { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaSync, FaEnvelope, FaServer } from 'react-icons/fa';
import { apiCall, ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import Toast from './Toast';
import { useToast } from '../hooks/useToast';
import { 
  EmailAccount, 
  EmailAccountFormData, 
  EmailProtocol, 
  EmailSecurity,
  EMAIL_DEFAULTS 
} from '../types';

interface UserEmailSettingsProps {
  userId: number;
}

const UserEmailSettings: React.FC<UserEmailSettingsProps> = ({ userId }) => {
  const { token } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [testingAccount, setTestingAccount] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<EmailAccountFormData>({
    name: '',
    email_address: '',
    protocol: 'imap',
    incoming_server: '',
    incoming_port: 993,
    incoming_security: 'ssl',
    smtp_server: '',
    smtp_port: 587,
    smtp_security: 'starttls',
    username: '',
    password: '',
    exchange_autodiscover: true,
    exchange_server: '',
    leave_messages_on_server: true,
    delete_after_days: 0,
    is_default: false,
    is_active: true,
    root_folder_path: 'INBOX',
    sync_enabled: true,
    sync_frequency: 5,
  });

  useEffect(() => {
    fetchAccounts();
  }, [userId, token]);

  const fetchAccounts = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await apiCall(
        `${ENDPOINTS.EMAIL.ACCOUNTS.LIST}?user_id=${userId}`,
        { method: 'GET' },
        token
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.results || data || []);
      }
    } catch (error) {
      console.error('Error fetching email accounts:', error);
      showToast('Failed to load email accounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProtocolChange = (protocol: EmailProtocol) => {
    const defaults = EMAIL_DEFAULTS[protocol];
    setFormData(prev => ({
      ...prev,
      protocol,
      ...defaults,
      // Reset Exchange settings when switching protocols
      exchange_autodiscover: protocol === 'exchange' || protocol === 'o365',
      exchange_server: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      const url = editingAccount
        ? ENDPOINTS.EMAIL.ACCOUNTS.UPDATE(editingAccount.id)
        : ENDPOINTS.EMAIL.ACCOUNTS.CREATE;
      
      const method = editingAccount ? 'PUT' : 'POST';
      
      const response = await apiCall(
        url,
        {
          method,
          body: JSON.stringify(formData),
        },
        token
      );

      if (response.ok) {
        showToast(
          editingAccount ? 'Email account updated successfully' : 'Email account created successfully',
          'success'
        );
        fetchAccounts();
        resetForm();
      } else {
        const errorData = await response.json();
        showToast(errorData.detail || 'Failed to save email account', 'error');
      }
    } catch (error) {
      console.error('Error saving email account:', error);
      showToast('Failed to save email account', 'error');
    }
  };

  const handleEdit = (account: EmailAccount) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      email_address: account.email_address,
      protocol: account.protocol,
      incoming_server: account.incoming_server,
      incoming_port: account.incoming_port,
      incoming_security: account.incoming_security,
      smtp_server: account.smtp_server,
      smtp_port: account.smtp_port,
      smtp_security: account.smtp_security,
      username: account.username,
      password: '', // Don't populate password for security
      exchange_autodiscover: account.exchange_autodiscover,
      exchange_server: account.exchange_server || '',
      leave_messages_on_server: account.leave_messages_on_server,
      delete_after_days: account.delete_after_days,
      is_default: account.is_default,
      is_active: account.is_active,
      root_folder_path: account.root_folder_path,
      sync_enabled: account.sync_enabled,
      sync_frequency: account.sync_frequency,
    });
    setShowForm(true);
  };

  const handleDelete = async (accountId: string) => {
    if (!token || !confirm('Are you sure you want to delete this email account?')) return;

    try {
      const response = await apiCall(
        ENDPOINTS.EMAIL.ACCOUNTS.DELETE(accountId),
        { method: 'DELETE' },
        token
      );

      if (response.ok) {
        showToast('Email account deleted successfully', 'success');
        fetchAccounts();
      } else {
        showToast('Failed to delete email account', 'error');
      }
    } catch (error) {
      console.error('Error deleting email account:', error);
      showToast('Failed to delete email account', 'error');
    }
  };

  const handleTestConnection = async (accountId: string) => {
    if (!token) return;

    try {
      setTestingAccount(accountId);
      const response = await apiCall(
        ENDPOINTS.EMAIL.ACCOUNTS.TEST(accountId),
        { method: 'POST' },
        token
      );

      const result = await response.json();
      
      if (result.success) {
        showToast(result.message, 'success');
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      showToast('Failed to test connection', 'error');
    } finally {
      setTestingAccount(null);
    }
  };

  const handleSetDefault = async (accountId: string) => {
    if (!token) return;

    try {
      const response = await apiCall(
        ENDPOINTS.EMAIL.ACCOUNTS.SET_DEFAULT(accountId),
        { method: 'POST' },
        token
      );

      if (response.ok) {
        showToast('Default account updated', 'success');
        fetchAccounts();
      } else {
        showToast('Failed to set default account', 'error');
      }
    } catch (error) {
      console.error('Error setting default account:', error);
      showToast('Failed to set default account', 'error');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingAccount(null);
    setFormData({
      name: '',
      email_address: '',
      protocol: 'imap',
      incoming_server: '',
      incoming_port: 993,
      incoming_security: 'ssl',
      smtp_server: '',
      smtp_port: 587,
      smtp_security: 'starttls',
      username: '',
      password: '',
      exchange_autodiscover: true,
      exchange_server: '',
      leave_messages_on_server: true,
      delete_after_days: 0,
      is_default: false,
      is_active: true,
      root_folder_path: 'INBOX',
      sync_enabled: true,
      sync_frequency: 5,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <FaSync className="animate-spin text-2xl text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold">Email Accounts</h3>
          <p className="text-sm text-base-content/70">
            Manage email accounts for sending and receiving emails
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary btn-sm"
          >
            <FaPlus className="mr-2" />
            Add Email Account
          </button>
        )}
      </div>

      {/* Account Form */}
      {showForm && (
        <div className="bg-base-200 p-6 rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">
                {editingAccount ? 'Edit Email Account' : 'New Email Account'}
              </h4>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-ghost btn-sm btn-circle"
              >
                <FaTimes />
              </button>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Account Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input input-bordered"
                  placeholder="e.g., Work Email"
                  required
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Email Address</span>
                </label>
                <input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                  className="input input-bordered"
                  placeholder="user@example.com"
                  required
                />
              </div>
            </div>

            {/* Protocol */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Protocol</span>
              </label>
              <select
                value={formData.protocol}
                onChange={(e) => handleProtocolChange(e.target.value as EmailProtocol)}
                className="select select-bordered"
              >
                <option value="imap">IMAP</option>
                <option value="pop3">POP3</option>
                <option value="exchange">Microsoft Exchange</option>
                <option value="o365">Office 365</option>
              </select>
            </div>

            {/* Exchange Settings */}
            {(formData.protocol === 'exchange' || formData.protocol === 'o365') && (
              <div className="space-y-4 p-4 bg-base-300 rounded-lg">
                <h5 className="font-semibold">Exchange Settings</h5>
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.exchange_autodiscover}
                      onChange={(e) => setFormData({ ...formData, exchange_autodiscover: e.target.checked })}
                      className="checkbox checkbox-primary"
                    />
                    <span className="label-text">Use Autodiscover</span>
                  </label>
                </div>
                {!formData.exchange_autodiscover && (
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Exchange Server URL</span>
                    </label>
                    <input
                      type="text"
                      value={formData.exchange_server}
                      onChange={(e) => setFormData({ ...formData, exchange_server: e.target.value })}
                      className="input input-bordered"
                      placeholder="https://exchange.company.com"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Server Settings */}
            <div className="grid grid-cols-2 gap-4">
              {/* Incoming Server */}
              <div className="space-y-4 p-4 bg-base-300 rounded-lg">
                <h5 className="font-semibold flex items-center gap-2">
                  <FaServer /> Incoming Server
                </h5>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Server</span>
                  </label>
                  <input
                    type="text"
                    value={formData.incoming_server}
                    onChange={(e) => setFormData({ ...formData, incoming_server: e.target.value })}
                    className="input input-bordered"
                    placeholder="imap.example.com"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Port</span>
                    </label>
                    <input
                      type="number"
                      value={formData.incoming_port}
                      onChange={(e) => setFormData({ ...formData, incoming_port: parseInt(e.target.value) })}
                      className="input input-bordered"
                      required
                    />
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Security</span>
                    </label>
                    <select
                      value={formData.incoming_security}
                      onChange={(e) => setFormData({ ...formData, incoming_security: e.target.value as EmailSecurity })}
                      className="select select-bordered"
                    >
                      <option value="none">None</option>
                      <option value="ssl">SSL/TLS</option>
                      <option value="starttls">STARTTLS</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Outgoing Server (SMTP) */}
              <div className="space-y-4 p-4 bg-base-300 rounded-lg">
                <h5 className="font-semibold flex items-center gap-2">
                  <FaEnvelope /> Outgoing Server (SMTP)
                </h5>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Server</span>
                  </label>
                  <input
                    type="text"
                    value={formData.smtp_server}
                    onChange={(e) => setFormData({ ...formData, smtp_server: e.target.value })}
                    className="input input-bordered"
                    placeholder="smtp.example.com"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Port</span>
                    </label>
                    <input
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                      className="input input-bordered"
                      required
                    />
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Security</span>
                    </label>
                    <select
                      value={formData.smtp_security}
                      onChange={(e) => setFormData({ ...formData, smtp_security: e.target.value as EmailSecurity })}
                      className="select select-bordered"
                    >
                      <option value="none">None</option>
                      <option value="ssl">SSL/TLS</option>
                      <option value="starttls">STARTTLS</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Authentication */}
            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Username</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="input input-bordered"
                  placeholder="Usually your email address"
                  required
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Password</span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input input-bordered"
                  placeholder={editingAccount ? 'Leave blank to keep current' : 'Enter password'}
                  required={!editingAccount}
                />
              </div>
            </div>

            {/* POP3 Settings */}
            {formData.protocol === 'pop3' && (
              <div className="alert alert-info">
                <span className="text-sm">
                  POP3 will always leave messages on the server (recommended for safety)
                </span>
              </div>
            )}

            {/* Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text">Default Account</span>
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text">Active</span>
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.sync_enabled}
                    onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.checked })}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text">Enable Sync</span>
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={resetForm} className="btn btn-ghost">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaCheck className="mr-2" />
                {editingAccount ? 'Update Account' : 'Add Account'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Accounts List */}
      <div className="space-y-3">
        {accounts.length === 0 ? (
          <div className="text-center py-8 text-base-content/60">
            No email accounts configured
          </div>
        ) : (
          accounts.map((account) => (
            <div
              key={account.id}
              className="bg-base-100 border border-base-300 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold text-lg">{account.name}</h4>
                    {account.is_default && (
                      <span className="badge badge-primary badge-sm">Default</span>
                    )}
                    {!account.is_active && (
                      <span className="badge badge-ghost badge-sm">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-base-content/70 mt-1">
                    <FaEnvelope className="inline mr-2" />
                    {account.email_address}
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-base-content/60">
                    <span className="badge badge-outline badge-sm">
                      {account.protocol.toUpperCase()}
                    </span>
                    <span>Server: {account.incoming_server}</span>
                    {account.last_sync && (
                      <span>Last sync: {new Date(account.last_sync).toLocaleString()}</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleTestConnection(account.id)}
                    disabled={testingAccount === account.id}
                    className="btn btn-ghost btn-sm"
                    title="Test Connection"
                  >
                    {testingAccount === account.id ? (
                      <FaSync className="animate-spin" />
                    ) : (
                      <FaSync />
                    )}
                  </button>
                  {!account.is_default && (
                    <button
                      onClick={() => handleSetDefault(account.id)}
                      className="btn btn-ghost btn-sm"
                      title="Set as Default"
                    >
                      <FaCheck />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(account)}
                    className="btn btn-ghost btn-sm"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(account.id)}
                    className="btn btn-ghost btn-sm text-error"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserEmailSettings;
